import type { ApiClient, ApiClientError } from '@acies/platform';
import { Cause, Data, Effect, Schema } from 'effect';
import { HttpBody, HttpClientRequest, HttpClientResponse } from 'effect/http';
import { type CalculationFailed, Calculations, type CalculationSucceeded, type ICalculation } from '../domain/calculations';
import { ConfigurationSelf } from '../domain/configurations';
import { ModelPosition, StockPosition } from '../domain/positions';
import { type ApiServices, runCall } from './runtime';
import { callDependencies, type EnginePath } from './shared';

/** The resource segment calculations live under, inside the BOM engine. */
const resource = 'calculations';

/** How long to leave the engine to it before asking after a calculation again. */
const askAgainAfter = '1 second';

/**
 * How long a calculation may take before we stop waiting for it.
 *
 * The engine has no upper bound of its own, and a row that waits for ever is a row that never says anything. A minute is long enough that a slow answer still arrives and short enough that a lost one is noticed.
 */
const giveUpAfter = '1 minute';

/**
 * What the engine prices a position by.
 *
 * `self` is all the Configurator sends here. The model, how big the window is, how many of it are wanted and the shape it is cut to all go to the configuration `self` names — the model, the measurements and the quantity with the request that makes it, the shape as one of its parameters — so the engine reads them from
 * there.
 *
 * `self` names the configuration the position is built from, so the engine prices the answers the user gave rather than the model's defaults. Typed by the very schema a configuration's own `self` is read with, so the two cannot disagree about what a configuration path looks like.
 */
const CalculationConfiguration = Schema.Struct({
	self: ConfigurationSelf,
});

/**
 * The body a calculation is asked for with.
 *
 * A `Schema.Struct` rather than a `Schema.Class`: it is built for one request and encoded straight onto it, so it is never held, never extended and never constructed by hand.
 */
const CreateCalculationBody = Schema.Struct({
	configuration: CalculationConfiguration,
	drawingEnabled: Schema.Literal(true),
	timeout: Schema.Literal(10),
	type: Schema.Literal('vip'),
});

/** The engine wraps its answer in a result, the way the Core API wraps collections in items. */
const CalculationResponse = Schema.Struct({ result: Calculations });

const encodeCreateCalculationBody = HttpClientRequest.schemaBodyJson(CreateCalculationBody);
const decodeCalculation = HttpClientResponse.schemaBodyJson(CalculationResponse);

/** Everything asking the engine to take on a calculation can fail with. */
export type RequestCalculationError = ApiClientError | HttpBody.HttpBodyError | Schema.SchemaError | UnconfiguredPositionError;

/**
 * Raised when a position is asked to be priced before it has been configured.
 *
 * A calculation names the configuration it prices by, so a position holding none has nothing to be priced by. The machine works a position up in the order that makes this impossible — configured first, priced second, and not priced at all when configuring failed — so reaching this is a fault in the caller rather than
 * a state to design for. It is an error rather than a thrown TypeError all the same, because a service answers on the error channel and its caller marks the row failed the same way it does for any other refusal.
 */
export class UnconfiguredPositionError extends Data.TaggedError('UnconfiguredPositionError')<{
	readonly id: string;
}> {}

/** Everything asking after a calculation can fail with. */
export type GetCalculationError = ApiClientError | Schema.SchemaError;

/**
 * Everything pricing a position can fail with.
 *
 * Both calls, since pricing a position makes both, plus running out of patience with an engine that never settles. A calculation the engine could not make is not among them: that is an answer, and it comes back as a position that says so.
 */
export type CreateCalculationError = Cause.TimeoutError | GetCalculationError | RequestCalculationError;

/**
 * Whether the engine is done with a calculation, however it went.
 *
 * Written as the two states that are an answer rather than as the two that are not, so a status the engine adds later is treated as unfinished until someone here decides otherwise — which is the safe way round: asking again costs a second, and taking a half-made calculation for a finished one costs a price.
 *
 * A type guard rather than a plain predicate, so what the wait answers with is narrowed by it: a calculation that has been waited for is one of the two that carry a result.
 */
function isSettled(calculation: ICalculation): calculation is CalculationFailed | CalculationSucceeded {
	return calculation.status === 'succeeded' || calculation.status === 'failed';
}

/**
 * The position as the calculation leaves it.
 *
 * A new position rather than the old one changed, because a position is a value: the row that shows it is handed the new one in place of the old, and anything still holding the old one is holding what was true when it got it.
 *
 * `listPrice` is what one of them costs, with any quantity discount already worked into it, so the total is that price as many times as the position asks for.
 *
 * Both endings name the calculation they came from, the failed one included: the engine took the job on and turned it down, so there is something to look the refusal up by. The failed one carries what the engine said about why as well, in the engine's own words — see `CalculationFailed` — and the succeeded one clears
 * whatever the position was carrying, since the answer it was carrying it for has now gone through.
 *
 * The succeeded ending carries the whole BOM over, not only the price read off it. The refused one leaves whatever the position had, which is what the spread does with the price as well: the two are written together and left together, so the measurements on screen are always the ones the price beside them was read
 * off.
 */
function priced<P extends PricedPosition>(position: P, calculation: CalculationFailed | CalculationSucceeded): P {
	const outcome =
		calculation.status === 'failed'
			? { calculationId: calculation.id, error: calculation.error, status: 'failed' as const }
			: {
					bom: calculation.bom,
					calculationId: calculation.id,
					error: null,
					price: calculation.listPrice,
					status: 'succeeded' as const,
					totalPrice: position.quantity * calculation.listPrice,
				};
	// asserted back to the kind it was handed: each branch builds that very kind, which is what the
	// caller asked for, but a class constructor cannot be chosen by a type parameter
	return (position instanceof ModelPosition ? new ModelPosition({ ...position, ...outcome }) : new StockPosition({ ...position, ...outcome })) as P;
}

/**
 * A position the engine prices: a model or a stock item, each by the configuration it holds.
 *
 * The two are priced alike — the engine is told the configuration and answers with a BOM — and come back as the kind they went in as.
 */
export type PricedPosition = ModelPosition | StockPosition;

/** The calls against the BOM engine's calculations. */
export interface CalculationCalls {
	/**
	 * Prices one position, waiting for the engine to finish.
	 *
	 * The two calls as one: the engine is asked to take the position on, and then asked after it every second until it has an answer. What comes back is the position as that answer leaves it — priced, or saying that it could not be priced.
	 */
	readonly createCalculation: <P extends PricedPosition>(position: P) => Effect.Effect<P, CreateCalculationError>;
	/**
	 * Asks the engine to take on one position.
	 *
	 * Answers as soon as the engine has taken the job, not once it is done: the calculation comes back `pending`, and what it costs is read by asking for it again by id.
	 *
	 * The position has to be a configured one, since the engine prices by the configuration's answers and is told which configuration by its `self`. One holding no configuration is refused with an {@link UnconfiguredPositionError} rather than priced as the model's defaults.
	 */
	readonly requestCalculation: (position: PricedPosition) => Effect.Effect<ICalculation, RequestCalculationError>;
	/**
	 * Asks after a calculation the engine was given earlier.
	 *
	 * The engine works on it after answering the POST, so this is what turns a `pending` answer into a priced one. It reports whatever the calculation is at the moment of asking, which means a caller waiting for a price asks again rather than waiting on this.
	 */
	readonly getCalculation: (id: string) => Effect.Effect<ICalculation, GetCalculationError>;
}

/** Implements the calculation calls over the client and BOM path every call shares. */
export function makeCalculationCalls(client: typeof ApiClient.Service, bomPath: EnginePath): CalculationCalls {
	/**
	 * Asks after a calculation until the engine has settled it, leaving it {@link askAgainAfter} between one answer and the next question.
	 *
	 * Asked again from inside the answer rather than through `Effect.repeat`: a repeat with a schedule is typed as any calculation, since a schedule that runs out ends it whether or not it settled, and narrowing that back would be a branch nothing can reach. Here the answer that has settled is the only way out, so it
	 * is what comes back. The wait between questions is a sleep, so a caller that is interrupted stops asking there.
	 */
	const askUntilSettled = (id: string): Effect.Effect<CalculationFailed | CalculationSucceeded, GetCalculationError> => calls.getCalculation(id).pipe(Effect.flatMap((asked) => (isSettled(asked) ? Effect.succeed(asked) : Effect.sleep(askAgainAfter).pipe(Effect.andThen(askUntilSettled(id))))));
	const calls: CalculationCalls = {
		createCalculation: (position) =>
			calls.requestCalculation(position).pipe(
				// the engine may have finished before it answered, in which case there is nothing to
				// wait for and nothing to ask again
				Effect.flatMap((calculation) => (isSettled(calculation) ? Effect.succeed(calculation) : askUntilSettled(calculation.id))),
				// around both calls, since what is being bounded is the wait for a price rather than
				// any one request. Each request has its own retries inside ApiClient
				Effect.timeout(giveUpAfter),
				Effect.map((calculation) => priced(position, calculation)),
			),
		requestCalculation: ({ configuration, id }) =>
			Effect.gen(function* () {
				// refused here rather than sent without a self: the engine would price the model's
				// defaults and answer as if it had priced the position, and a wrong price looks exactly
				// like a right one
				if (configuration === null) {
					return yield* Effect.fail(new UnconfiguredPositionError({ id }));
				}
				const path = yield* bomPath(resource);
				const request = yield* encodeCreateCalculationBody(HttpClientRequest.post(path), {
					configuration: {
						self: configuration.self,
					},
					drawingEnabled: true,
					timeout: 10,
					type: 'vip',
				});
				const response = yield* client.execute(request);
				const { result } = yield* decodeCalculation(response);
				return result;
			}),
		getCalculation: (id) =>
			Effect.gen(function* () {
				const path = yield* bomPath(`${resource}/${id}`);
				const response = yield* client.get(path);
				const { result } = yield* decodeCalculation(response);
				return result;
			}),
	};
	return calls;
}

/**
 * Prices one position, from outside Effect, waiting for the engine to finish.
 *
 * The awaitable form of {@link CalculationCalls.createCalculation}, for the components that press the button. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — because what stopped a position being priced is what the row has to say. An answer is the position as the engine leaves it;
 * anything else is a {@link CreateCalculationError}.
 */
export const createCalculation = <P extends PricedPosition>(position: P): Promise<CreateCalculationError | P> => runCall(() => createCalculationEffect(position));

/**
 * Prices one position, waiting for the engine to finish, as an Effect over the Configurator's services.
 *
 * For a caller that forks the call rather than awaiting it, so a price overtaken by a change to the position can be interrupted — see `ConfiguratorMachine`. Interrupted, the request out is aborted and the engine is not asked after it again; a calculation the engine has already taken on is left to finish there, unread.
 */
export const createCalculationEffect = <P extends PricedPosition>(position: P): Effect.Effect<P, CreateCalculationError, ApiServices> => callDependencies.pipe(Effect.flatMap(({ bomPath, client }) => makeCalculationCalls(client, bomPath).createCalculation(position)));

/**
 * Asks after a calculation, from outside Effect.
 *
 * The awaitable form of {@link CalculationCalls.getCalculation}, for the components that wait for a price. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — and is distinct from a calculation that answered and is still `pending`, which is an answer rather than a failure.
 */
export const getCalculation = (id: string): Promise<GetCalculationError | ICalculation> => runCall(({ bomPath, client }) => makeCalculationCalls(client, bomPath).getCalculation(id));
